<template>
  <div v-if="items.length" class="quick-access-menu">
    <b-dropdown
      variant="primary"
      right
      no-caret
      toggle-class="quick-access-menu__toggle"
      menu-class="quick-access-menu__menu"
    >
      <template v-slot:button-content>
        <span
          class="quick-access-menu__toggle-content"
          v-b-tooltip.hover.bottom
          title="Accesos rápidos: vistas y herramientas disponibles en este módulo"
        >
          <AppIcon name="lightning" font-scale="1.15" weight="500" />
          <span class="quick-access-menu__toggle-text">Accesos</span>
          <AppIcon name="chevron-down" class="quick-access-menu__toggle-caret" font-scale="1" weight="600" />
        </span>
      </template>

      <template v-for="(section, sectionIndex) in sections">
        <b-dropdown-divider v-if="sectionIndex > 0" :key="'div-' + sectionIndex" />
        <b-dropdown-header :key="'title-' + sectionIndex" class="quick-access-menu__group-title">
          {{ section.title }}
        </b-dropdown-header>
        <b-dropdown-item
          v-for="item in section.items"
          :key="item.id"
          class="quick-access-menu__item"
          @click="select(item)"
        >
          <span class="quick-access-menu__item-icon" :class="'is-' + section.kind">
            <AppIcon :name="item.icon || defaultIconByKind(section.kind)" font-scale="1.3" weight="500" />
          </span>
          <span class="quick-access-menu__item-content">
            <strong>{{ item.title }}</strong>
            <small v-if="item.description">{{ item.description }}</small>
          </span>
        </b-dropdown-item>
      </template>
    </b-dropdown>
  </div>
</template>

<script>
  export default {
    name: 'QuickAccessMenu',
    props: {
      items: {
        type: Array,
        default: () => []
      }
    },
    computed: {
      systemItems() {
        return this.items.filter(item => !item.dynamic)
      },
      phpItems() {
        return this.items.filter(item => item.dynamic)
      },
      ungroupedItems() {
        return this.systemItems.filter(item => !item.group)
      },
      namedGroups() {
        const order = []
        const groups = {}
        this.systemItems.filter(item => item.group).forEach(item => {
          const key = item.group === true ? '__default__' : item.group
          if(!groups[key]) {
            groups[key] = { name: item.group === true ? 'Otras configuraciones' : item.group, items: [] }
            order.push(key)
          }
          groups[key].items.push(item)
        })
        return order.map(key => groups[key])
      },
      sections() {
        const result = []
        if(this.ungroupedItems.length) result.push({ title: 'Accesos del sistema', items: this.ungroupedItems, kind: 'system' })
        if(this.phpItems.length) result.push({ title: 'Accesos personalizados', items: this.phpItems, kind: 'php' })
        this.namedGroups.forEach(group => result.push({ title: group.name, items: group.items, kind: 'group' }))
        return result
      }
    },
    methods: {
      select(item) {
        this.$emit('select', item)
      },
      defaultIconByKind(kind) {
        if(kind === 'php') return 'code'
        if(kind === 'group') return 'settings'
        return 'dashboard'
      }
    }
  }
</script>

<style scoped>
  .quick-access-menu {
    display: inline-flex;
  }
  .quick-access-menu >>> .quick-access-menu__toggle {
    display: inline-flex;
    align-items: center;
    height: 34px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    background: var(--app-color-primary);
    box-shadow: 0 4px 12px rgba(11, 31, 59, 0.2);
    transition: background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  }
  .quick-access-menu >>> .quick-access-menu__toggle:hover,
  .quick-access-menu >>> .quick-access-menu__toggle:focus {
    background: var(--app-color-primary-hover);
    box-shadow: 0 6px 16px rgba(11, 31, 59, 0.26);
    transform: translateY(-1px);
  }
  .quick-access-menu >>> .quick-access-menu__toggle:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.7), 0 0 0 6px rgba(0, 111, 140, 0.32);
  }
  .quick-access-menu__toggle-content {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
  .quick-access-menu__toggle-content .app-icon {
    color: var(--app-color-on-primary);
  }
  .quick-access-menu__toggle-text {
    color: var(--app-color-on-primary);
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-semibold);
    white-space: nowrap;
  }
  .quick-access-menu__toggle-caret {
    opacity: 0.85;
  }

  .quick-access-menu >>> .quick-access-menu__menu {
    min-width: 380px;
    max-width: 440px;
    max-height: 70vh;
    overflow-y: auto;
    padding: 8px;
    border: 1px solid var(--app-color-border);
    border-radius: 12px;
    box-shadow: var(--app-shadow-md);
  }
  .quick-access-menu >>> .quick-access-menu__group-title .dropdown-header {
    padding: 8px 10px 6px;
    background: transparent !important;
    border: 0 !important;
    color: var(--app-color-text-muted);
    font-size: 11px;
    font-weight: var(--app-font-semibold);
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }
  .quick-access-menu >>> .quick-access-menu__item .dropdown-item {
    display: flex !important;
    align-items: center;
    gap: 12px;
    padding: 9px 10px;
    border-radius: 9px;
    white-space: normal !important;
  }
  .quick-access-menu >>> .quick-access-menu__item .dropdown-item:hover,
  .quick-access-menu >>> .quick-access-menu__item .dropdown-item:focus {
    background: var(--app-color-primary-soft);
  }
  .quick-access-menu__item-icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary-active);
  }
  .quick-access-menu__item-icon.is-php {
    background: #fdeec8;
    color: var(--app-color-warning-hover);
  }
  .quick-access-menu__item-icon.is-group {
    background: var(--app-color-surface-muted);
    color: var(--app-color-text-secondary);
  }
  .quick-access-menu__item-content {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .quick-access-menu__item-content strong {
    color: var(--app-color-text);
    font-size: var(--app-text-sm);
    font-weight: var(--app-font-semibold);
  }
  .quick-access-menu__item-content small {
    margin-top: 2px;
    color: var(--app-color-text-muted);
    font-size: var(--app-text-xs);
    line-height: 1.35;
  }
  @media (max-width: 575.98px) {
    .quick-access-menu >>> .quick-access-menu__menu {
      min-width: 92vw;
      max-width: 92vw;
    }
  }
  @media (max-width: 767.98px) {
    .quick-access-menu >>> .quick-access-menu__toggle {
      height: 34px;
      padding: 0 10px;
    }
  }
</style>
